import type { Address } from '@paid-ui/schemas/zod/address';

import { forwardRef, useEffect, useRef } from 'react';
import { cva } from 'class-variance-authority';
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary';
import { twMerge } from 'tailwind-merge';
import { getDetails, getGeocode, getLatLng } from 'use-places-autocomplete';

import { CountryCode } from '@paid-ui/enums/address';
import { useMergedRef } from '@paid-ui/hooks/use-merged-ref';

import { formatAddress } from '../place-autocomplete';

const containerVariants = cva('h-60 w-full rounded-md border border-grey-mid bg-grey-light', {
  variants: {
    fallback: {
      true: 'flex flex-col items-center justify-center gap-y-2 p-4 text-center text-grey-dark',
    },
  },
});

const defaultMapOptions = {
  zoom: 3,
  center: {
    lat: -24.15,
    lng: 133.25,
  },
  mapTypeControl: false,
  fullscreenControl: false,
  streetViewControl: false,
  controlSize: 24,
} as google.maps.MapOptions;

const defaultMarkerOptions = (
  globalThis.window !== undefined && globalThis.google
    ? {
        animation: google.maps.Animation.DROP,
      }
    : {}
) as google.maps.MarkerOptions;

const getInfoWindowContent = (details: string | google.maps.places.PlaceResult) => {
  return `<div>${typeof details === 'string' ? details : details.formatted_address}</div>`;
};

export interface GoogleMapsMarkerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  value?: Address | '';
  region?: CountryCode;
  language?: string;
  zoom?: number;
  mapId?: string;
  mapOptions?: google.maps.MapOptions;
  markerOptions?: google.maps.MarkerOptions;
}

const BaseGoogleMapsMarker = forwardRef<HTMLDivElement, GoogleMapsMarkerProps>(
  (
    {
      value,
      region = CountryCode.AU,
      language = 'en-AU',
      zoom = 14,
      mapId,
      mapOptions = {},
      markerOptions = {},

      hidden,
      className,

      ...restProps
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.Marker | null>(null);
    const markerClickRef = useRef<google.maps.MapsEventListener | null>(null);
    const { showBoundary } = useErrorBoundary();

    const address = formatAddress(value);
    const mergedContainerRef = useMergedRef(containerRef, ref);
    const mergedMapOptions = { ...defaultMapOptions, ...mapOptions, mapId };
    const mergedMarkerOptions = { ...defaultMarkerOptions, ...markerOptions };

    const resetMap = () => {
      markerRef.current?.setMap(null);
      markerRef.current = null;
      mapRef.current?.setOptions(mergedMapOptions);
    };

    const decodeAddressAsMarker = async () => {
      try {
        if (!address) {
          resetMap();
          return;
        }

        const [geoCode] = await getGeocode({ address, language, region });

        if (!geoCode) {
          resetMap();
          return;
        }

        const position = getLatLng(geoCode);

        markerRef.current?.setMap(null);
        markerRef.current = new google.maps.Marker({
          position,
          map: mapRef.current,
          // FIXME: provide map pin icon based on theme
          icon: '/map-pin-blue.png',
          ...mergedMarkerOptions,
        });

        mapRef.current?.setCenter(position);
        mapRef.current?.setZoom(zoom);
        markerClickRef.current = markerRef.current?.addListener('click', async () => {
          const details = await getDetails({
            placeId: geoCode.place_id,
            region,
            language,
          });
          const content = getInfoWindowContent(details);
          const infoWindow = new google.maps.InfoWindow({ content });
          mapRef.current?.setCenter(position);
          mapRef.current?.setZoom(zoom);
          infoWindow.open(mapRef.current, markerRef.current);
        });
      } catch (error) {
        showBoundary(
          error instanceof Error ? error : new Error('Failed to decode address as marker'),
        );
      }
    };

    // Initialize map when map options change
    useEffect(() => {
      if (!globalThis.google) {
        showBoundary(new Error('Google Maps API is not loaded'));
        return;
      }

      if (!containerRef.current) {
        showBoundary(new Error('Google Maps container is not ready'));
        return;
      }

      if (mapRef.current) {
        mapRef.current.setOptions(mapOptions);
      } else {
        mapRef.current = new google.maps.Map(containerRef.current, mergedMapOptions);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mergedMapOptions]);

    // Decode address as marker when address changes
    useEffect(() => {
      if (!globalThis.google) {
        showBoundary(new Error('Google Maps API is not loaded'));
        return;
      }
      decodeAddressAsMarker();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address]);

    // Clean up
    useEffect(() => {
      return () => {
        markerRef.current?.setMap(null);
        markerRef.current = null;
        mapRef.current = null;
        if (markerClickRef.current) {
          google.maps.event.removeListener(markerClickRef.current);
        }
      };
    }, []);

    if (hidden) {
      return null;
    }

    return (
      <div
        ref={mergedContainerRef}
        className={twMerge(containerVariants({ className }))}
        {...restProps}
      />
    );
  },
);

const GoogleMapsFallback = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { error: unknown }
>(({ error, className, ...restProps }, ref) => {
  return (
    <div
      ref={ref}
      className={twMerge(
        containerVariants({
          fallback: true,
          className,
        }),
      )}
      {...restProps}
    >
      <p className="font-medium">Something went wrong.</p>
      <pre className="whitespace-pre-wrap text-sm text-pink">
        {error instanceof Error ? error.message : 'Unknown error'}
      </pre>
    </div>
  );
});

export const GoogleMapsMarker = forwardRef<HTMLDivElement, GoogleMapsMarkerProps>(
  ({ hidden, id, mapId, style, className, ...restProps }, ref) => {
    if (hidden) {
      return null;
    }

    return (
      <ErrorBoundary
        fallbackRender={({ error }) => (
          <GoogleMapsFallback error={error} id={id} style={style} className={className} />
        )}
      >
        <BaseGoogleMapsMarker
          ref={ref}
          id={id}
          mapId={mapId}
          style={style}
          className={className}
          {...restProps}
        />
      </ErrorBoundary>
    );
  },
);

BaseGoogleMapsMarker.displayName = 'BaseGoogleMapsMarker';
GoogleMapsMarker.displayName = 'GoogleMapsMarker';
GoogleMapsFallback.displayName = 'GoogleMapsFallback';
